import {
  Play,
  Pause,
  SkipForward,
  Volume2,
  VolumeX,
  Loader2,
  Radio as RadioIcon,
  ExternalLink,
} from "lucide-react";

// --- Helpers (suppression des ternaires imbriqués) --------------------------

function PlayBigIcon({ isLoading, isPlaying }) {
  if (isLoading) return <Loader2 size={20} className="rh-spin" />;
  if (isPlaying) return <Pause size={20} />;
  return <Play size={20} />;
}

function VolumeIcon({ muted, volume }) {
  if (muted || volume === 0) return <VolumeX size={18} />;
  return <Volume2 size={18} />;
}

// --- Sous-composants --------------------------------------------------------

function PlayerInfo({ radio, meta }) {
  // Idem RadioCard : si flux figé > 5min, on bascule sur le logo et on masque
  // les métadonnées obsolètes.
  const stale = !!meta?.stale;
  const cover = stale ? radio.logo : (meta?.artwork || radio.logo);
  const title = stale ? null : meta?.title;
  const artist = stale ? null : meta?.artist;

  return (
    <div className="rh-player-left">
      <div className="rh-player-art">
        <img
          src={cover}
          alt={radio.name}
          onError={(e) => (e.currentTarget.src = radio.logo)}
        />
      </div>
      <div className="rh-player-info">
        <div className="rh-player-name">
          {radio.name}
          {radio.external_url && (
            <a
              href={radio.external_url}
              target="_blank"
              rel="noreferrer"
              aria-label="Ouvrir la PWA"
              className="rh-player-ext"
              data-testid="player-ext-link"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
        <PlayerTrack title={title} artist={artist} tagline={radio.tagline} />
      </div>
    </div>
  );
}

function PlayerTrack({ title, artist, tagline }) {
  if (title || artist) {
    return (
      <div className="rh-player-track">
        <span className="rh-marquee">{title || "—"}</span>
        {artist && <span className="rh-player-artist"> · {artist}</span>}
      </div>
    );
  }
  return (
    <div className="rh-player-track">
      <span className="rh-player-artist">{tagline}</span>
    </div>
  );
}

function PlayerControls({ isPlaying, isLoading, onTogglePlay, onZap }) {
  return (
    <div className="rh-player-controls">
      <button
        type="button"
        className="rh-btn rh-btn-play"
        onClick={onTogglePlay}
        data-testid="player-play-button"
        aria-label={isPlaying ? "Pause" : "Lecture"}
      >
        <PlayBigIcon isLoading={isLoading} isPlaying={isPlaying} />
      </button>
      <button
        type="button"
        className="rh-btn rh-btn-zap"
        onClick={onZap}
        data-testid="player-zap-button"
        aria-label="Zapper à la radio suivante"
      >
        <SkipForward size={18} />
        <span className="rh-zap-label">Zap</span>
      </button>
    </div>
  );
}

function VolumeControls({ volume, muted, onVolume, onMute }) {
  return (
    <div className="rh-player-right">
      <button
        type="button"
        className="rh-btn rh-btn-icon"
        onClick={onMute}
        aria-label="Muet"
        data-testid="player-mute-button"
      >
        <VolumeIcon muted={muted} volume={volume} />
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={muted ? 0 : volume}
        onChange={(e) => onVolume(parseFloat(e.target.value))}
        className="rh-volume"
        data-testid="player-volume-slider"
        aria-label="Volume"
      />
    </div>
  );
}

// --- Composant principal ----------------------------------------------------

export function Player({
  radio,
  meta,
  isPlaying,
  isLoading,
  volume,
  muted,
  onTogglePlay,
  onZap,
  onVolume,
  onMute,
}) {
  if (!radio) {
    return (
      <div className="rh-player rh-player-empty" data-testid="player-bar-empty">
        <RadioIcon size={18} />
        <span>Choisissez une station pour commencer</span>
      </div>
    );
  }
  return (
    <div className="rh-player" data-testid="player-bar" style={{ "--accent": radio.color }}>
      <PlayerInfo radio={radio} meta={meta} />
      <PlayerControls
        isPlaying={isPlaying}
        isLoading={isLoading}
        onTogglePlay={onTogglePlay}
        onZap={onZap}
      />
      <VolumeControls volume={volume} muted={muted} onVolume={onVolume} onMute={onMute} />
    </div>
  );
}
